import { Box, Button, FormField, Input, SpaceBetween } from "@amzn/awsui-components-react";
import { FC, useState } from "react";
import { MapEngineHandle } from "../../react";

export interface BboxPanelProps {
    handle: MapEngineHandle | null;
}

const SOURCE_ID = "viewer-v2-bbox";
const LAYER_IDS = [`${SOURCE_ID}-fill`, `${SOURCE_ID}-line`];
const BBOX_COLOR = "#16191f";

const num = (s: string) => parseFloat(s);

/**
 * Overlays a bounding box from west/south/east/north coords and fits the map to
 * it. Uses the v2 engine overlay methods. NOTE: interactive draw-on-map (1.0
 * used MapboxDraw) is deferred to a follow-up that integrates terra-draw; this
 * panel covers the enter-coords + visualize + fit case.
 */
const BboxPanel: FC<BboxPanelProps> = ({ handle }) => {
    const [w, setW] = useState("-122.46");
    const [s, setS] = useState("47.49");
    const [e, setE] = useState("-122.22");
    const [n, setN] = useState("47.73");

    const coords = [num(w), num(s), num(e), num(n)];
    const valid = coords.every((c) => !Number.isNaN(c)) && num(w) < num(e) && num(s) < num(n);

    const plot = () => {
        const engine = handle?.engine();
        if (!engine || !valid) return;
        const polygon = {
            type: "FeatureCollection",
            features: [
                {
                    type: "Feature",
                    properties: {},
                    geometry: {
                        type: "Polygon",
                        coordinates: [[
                            [num(w), num(s)], [num(e), num(s)], [num(e), num(n)],
                            [num(w), num(n)], [num(w), num(s)],
                        ]],
                    },
                },
            ],
        };
        if (!engine.setGeoJsonSource(SOURCE_ID, polygon)) return;
        engine.addOverlayLayer({
            id: LAYER_IDS[0], type: "fill", source: SOURCE_ID,
            paint: { "fill-color": BBOX_COLOR, "fill-opacity": 0.1 },
        });
        engine.addOverlayLayer({
            id: LAYER_IDS[1], type: "line", source: SOURCE_ID,
            paint: { "line-color": BBOX_COLOR, "line-width": 2 },
        });
        // Center on the bbox.
        engine.flyTo({ lng: (num(w) + num(e)) / 2, lat: (num(s) + num(n)) / 2, animate: false });
    };

    const clear = () => handle?.engine()?.removeOverlay(SOURCE_ID, LAYER_IDS);

    return (
        <SpaceBetween size="s">
            <Box variant="small">Enter a bounding box (WGS84) to overlay it.</Box>
            <FormField label="West (min lng)"><Input value={w} onChange={({ detail }) => setW(detail.value)} /></FormField>
            <FormField label="South (min lat)"><Input value={s} onChange={({ detail }) => setS(detail.value)} /></FormField>
            <FormField label="East (max lng)"><Input value={e} onChange={({ detail }) => setE(detail.value)} /></FormField>
            <FormField label="North (max lat)"><Input value={n} onChange={({ detail }) => setN(detail.value)} /></FormField>
            <SpaceBetween direction="horizontal" size="xs">
                <Button variant="primary" disabled={!valid} onClick={plot}>Plot</Button>
                <Button onClick={clear}>Clear</Button>
            </SpaceBetween>
        </SpaceBetween>
    );
};

export default BboxPanel;
